import React, { useEffect, useState, useRef } from "react";
import {
  X,
  Download,
  Save,
  Edit,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API_END_POINTS } from "../network/apiEndPoint";
import { useSelector } from "react-redux";
import { getProtected } from "../network/ApiService";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Carousel from "./Carousel";
import BookValueHistory from "./common/BookValueHistory";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ContractHistoryTable from "./ContractHistoryTable";
import { baseURlNoAPI } from "../network/baseUrl";
import HeroSection from "./HeroSection/HeroSection";
import ContractHistoryView from "./ContractHistoryView";
import defaultImage from "../assets/photo.png";
import CarouselDetailView from "./CarouselDetailView";
import Loading from "../SharedComponent/Loading";
import logo from "../assets/martin.png";
import S3Image from "./common/S3Image";
import { getDownloadUrl } from "../network/S3ApiService";
import { toast } from "react-toastify";
import { formatYearOfManufacture } from "../utils/yearOfManufacture";
import {
  getAttachmentLargeUrl,
  getAttachmentThumbUrl,
  getImageAttachments,
  fetchAttachmentImageDataUrl,
  resolveMediaUrl,
} from "../utils/attachmentImages";

const buildPdfImageGallery = async (clonedElement, imageAttachments) => {
  const imageSection = clonedElement.querySelector("[data-pdf-image-section]");
  if (!imageSection) return;

  try {
    if (imageAttachments.length === 0) return;

    if (imageAttachments.length === 1) {
      const dataUrl = await fetchAttachmentImageDataUrl(
        imageAttachments[0].id,
        "large"
      );
      imageSection.innerHTML = `
        <div style="margin-bottom:16px;">
          <img
            src="${dataUrl}"
            alt="Forklift view"
            style="height:400px;width:100%;object-fit:contain;border-radius:8px;background:#f9fafb;display:block;"
          />
        </div>
      `;
      return;
    }

    const [firstAttachment, ...restAttachments] = imageAttachments;
    const mainDataUrl = await fetchAttachmentImageDataUrl(
      firstAttachment.id,
      "large"
    );
    const thumbDataUrls = await Promise.all(
      restAttachments.map((attachment) =>
        fetchAttachmentImageDataUrl(attachment.id, "thumb")
      )
    );

    const thumbsHtml = thumbDataUrls
      .map(
        (url, index) => `
        <div style="width:64px;height:64px;border-radius:8px;overflow:hidden;border:2px solid #e5e7eb;flex-shrink:0;background:#f3f4f6;">
          <img
            src="${url}"
            alt="Forklift view ${index + 2}"
            style="width:100%;height:100%;object-fit:cover;display:block;"
          />
        </div>
      `
      )
      .join("");

    imageSection.innerHTML = `
      <div style="background:#f9fafb;border-radius:8px;overflow:hidden;height:400px;width:100%;margin-bottom:16px;display:flex;align-items:center;justify-content:center;">
        <img
          src="${mainDataUrl}"
          alt="Forklift view 1"
          style="max-height:100%;max-width:100%;object-fit:contain;display:block;"
        />
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:24px;">
        ${thumbsHtml}
      </div>
    `;
  } catch (error) {
    console.error("Failed to build PDF image gallery:", error);
  }
};

const inlineLocalImagesForPdf = async (root) => {
  const images = root.querySelectorAll("img");
  await Promise.all(
    [...images].map(async (img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return;
      if (src.includes("your-objectstorage.com")) return;

      try {
        const response = await fetch(src);
        if (!response.ok) return;
        const blob = await response.blob();
        img.src = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        // Keep original src for same-origin assets html2canvas may still read.
      }
    })
  );
};

export const ForkliftDetailView = (props) => {
  const { id } = useParams();
  const { setViewAssetId } = props;
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const { currentLanguages } = useSelector((state) => state.language);
  const navigate = useNavigate();
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { t } = useTranslation();
  const contentRef = useRef(null);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactWebsite, setContactWebsite] = useState("");
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactPhoneCountry, setContactPhoneCountry] = useState("+49");
  const [contactPhoneNumber, setContactPhoneNumber] = useState("");
  const [remark, setRemark] = useState("");
  const [imageAttachments, setImageAttachments] = useState([]);
  const [loadedFullImageIndices, setLoadedFullImageIndices] = useState(
    () => new Set([0])
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentThumbnailStartIndex, setCurrentThumbnailStartIndex] =
    useState(0);
  const thumbnailsVisible = 6;
  const thumbnailStepRem = 4.5; // 4rem thumb + 0.5rem gap

  const handlePrevThumbnails = () => {
    setCurrentThumbnailStartIndex(
      (prev) => Math.max(0, prev - 1) // Move one image at a time
    );
  };

  const handleNextThumbnails = () => {
    setCurrentThumbnailStartIndex((prev) =>
      Math.min(imageAttachments.length - thumbnailsVisible, prev + 1)
    );
  };

  useEffect(() => {
    setCurrentThumbnailStartIndex(0);
    setCurrentImageIndex(0);
    setLoadedFullImageIndices(new Set([0]));
  }, [imageAttachments]);

  useEffect(() => {
    setLoadedFullImageIndices((prev) => {
      if (prev.has(currentImageIndex)) return prev;
      const next = new Set(prev);
      next.add(currentImageIndex);
      return next;
    });
  }, [currentImageIndex]);

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    if (index < currentThumbnailStartIndex) {
      setCurrentThumbnailStartIndex(index);
    } else if (index >= currentThumbnailStartIndex + thumbnailsVisible) {
      setCurrentThumbnailStartIndex(
        Math.max(0, index - thumbnailsVisible + 1)
      );
    }
  };

  const thumbnailSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    rows: 3,
    slidesPerRow: 1,
    arrows: false,
    autoplay: false,
  };

  function formatToEuropean(number) {
    return Number(number).toLocaleString("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  const batteryTypes = [
    { value: "", option: t("createAsset.placeholders.batteryTypeExplanation") },
    { value: 1, option: t("assets.options.none") },
    { value: 2, option: t("assets.options.pzs") },
    { value: 3, option: t("assets.options.gel") },
    { value: 4, option: t("assets.options.lithium-ion") },
    { value: 5, option: t("assets.options.starter") },
    { value: 6, option: t("assets.options.waterless") },
    { value: 7, option: t("assets.options.tppl") },
    { value: 8, option: t("assets.options.nimh") },
    { value: 9, option: t("assets.options.battery_others") },
  ];
  const conditions = [
    {
      value: "",
      option: t("createAsset.placeholders.selectTechnicalCondition"),
    },
    { value: 2, option: t("createAsset.technicalCondition.very_good") },
    { value: 3, option: t("createAsset.technicalCondition.good") },
    { value: 4, option: t("createAsset.technicalCondition.used") },
    { value: 5, option: t("createAsset.technicalCondition.bad") },
    { value: 6, option: t("createAsset.technicalCondition.very_bad") },
    { value: 7, option: t("createAsset.technicalCondition.new") },
  ];
  const tireTypes = [
    { value: 0, option: t("assets.options.unknown") },
    { value: 1, option: t("assets.options.cushion") },
    { value: 2, option: t("assets.options.pneumatic") },
    { value: 3, option: t("assets.options.polyurethane") },
    { value: 4, option: t("assets.options.superelastic") },
    { value: 5, option: t("assets.options.solid") },
    { value: 6, option: t("assets.options.vulkollan") },
  ];

  const engineTypeOptions = [
    { value: "Diesel", option: t("assets.options.diesel") },
    { value: "Electric", option: t("assets.options.electric") },
    { value: "LPG", option: t("assets.options.lpg") },
    { value: "Gasoline", option: t("assets.options.gasoline-gas") },
    { value: "Hybrid", option: t("assets.options.hybrid") },
    { value: "Fuel-Cell", option: t("assets.options.fuel-cell") },
    { value: "Li-Ion", option: t("assets.options.lithium-ion") },
    { value: "manual/none", option: t("assets.options.manual/none") },
    { value: "Natural gas (CNG)", option: t("assets.options.natural-gas") },
  ];

  const chargerTypes = [
    { value: "", option: t("createAsset.placeholders.chargerTypeExplanation") },
    { value: 1, option: t("assets.options.single-phase") },
    { value: 2, option: t("assets.options.three-phase") },
    { value: 3, option: t("assets.options.hf") },
    { value: 4, option: t("assets.options.modular") },
    { value: 5, option: t("assets.options.opportunity") },
    { value: 6, option: t("assets.options.li-ferro") },
    { value: 7, option: t("assets.options.scr") },
    { value: 8, option: t("assets.options.charger_others") },
  ];
  // Add this helper function around line 150 with your other helper functions:

  const getMastClassification = (value) => {
    const mastTypes = [
      { value: 0, option: t("assets.fields.selectClassification") },
      { value: 1, option: t("assetDetails.mastTypes.standard") },
      { value: 2, option: t("assetDetails.mastTypes.mono") },
      { value: 3, option: t("assetDetails.mastTypes.duplex") },
      { value: 5, option: t("assetDetails.mastTypes.triplex") },
      { value: 8, option: t("assetDetails.mastTypes.quattro") },
      { value: 9, option: t("assetDetails.mastTypes.telescopic") },
    ];
    const mastType = mastTypes.find((type) => type.value == value);
    return mastType ? mastType.option : value;
  };

  // Add this helper function with your other helper functions around line 160
  const getAttachmentsClassification = (value) => {
    const attachmentTypes = [
      { value: "", option: t("assets.fields.selectClassification") },
      { value: 1, option: t("attachments.types.loadPushUnit") },
      { value: 2, option: t("attachments.types.baleClamps") },
      { value: 3, option: t("attachments.types.baleClamps360") },
      { value: 4, option: t("attachments.types.pulpBaleClamps360") },
      { value: 5, option: t("attachments.types.boxDischargeUnit") },
      { value: 6, option: t("attachments.types.multiPalletHandlerProfile") },
      { value: 7, option: t("attachments.types.poleTineClamps") },
      { value: 8, option: t("attachments.types.twoWayForkClamps") },
      { value: 9, option: t("attachments.types.twoWayForkClamps360") },
      { value: 10, option: t("attachments.types.rotators360") },
      { value: 11, option: t("attachments.types.rotators360BoltOn") },
      { value: 12, option: t("attachments.types.dischargeUnit") },
      { value: 13, option: t("attachments.types.rotatingJawClamps") },
      { value: 14, option: t("attachments.types.drumClamps") },
      { value: 15, option: t("attachments.types.drumClamps360") },
      { value: 16, option: t("attachments.types.harbourClamps") },
      { value: 17, option: t("attachments.types.applianceClampsCirculating") },
      { value: 18, option: t("attachments.types.applianceClampsRigid") },
      { value: 19, option: t("attachments.types.forkClamps") },
      { value: 20, option: t("attachments.types.forkClamps360") },
      { value: 21, option: t("attachments.types.loadPushPulls") },
      { value: 22, option: t("attachments.types.jibCrane") },
      { value: 23, option: t("attachments.types.loadStabilizers") },
      { value: 24, option: t("attachments.types.multiPalletHandlers21") },
      { value: 25, option: t("attachments.types.palletHandlersLoadExtender") },
      {
        value: 26,
        option: t("attachments.types.multiPalletHandlerProfile"),
      },
      {
        value: 27,
        option: t("attachments.types.multiPalletHandlers321"),
      },
      {
        value: 28,
        option: t("attachments.types.multiPalletHandlers4"),
      },
      {
        value: 29,
        option: t("attachments.types.multiPalletHandlers42"),
      },
      {
        value: 30,
        option: t("attachments.types.multiPalletHandlers64"),
      },
      { value: 31, option: t("attachments.types.palletInverters360") },
      { value: 32, option: t("attachments.types.loadExtenders") },
      { value: 33, option: t("attachments.types.paperRollClamps360") },
      { value: 34, option: t("attachments.types.paperRollClamps180") },
      {
        value: 35,
        option: t("attachments.types.paperRollClamps360Arm"),
      },
      {
        value: 36,
        option: t("attachments.types.paperRollClamps180Arm"),
      },
      { value: 37, option: t("attachments.types.rollUpender90") },
      {
        value: 38,
        option: t("attachments.types.rollUpender90With360"),
      },
      { value: 39, option: t("attachments.types.tyreClamps360") },
      { value: 40, option: t("attachments.types.foamClamps") },
      {
        value: 41,
        option: t("attachments.types.hoseReelsSupportFlex"),
      },
      { value: 42, option: t("attachments.types.hydraulicScoops") },
      { value: 43, option: t("attachments.types.sideshifts") },
      { value: 44, option: t("attachments.types.sideshiftsDual") },
      { value: 45, option: t("attachments.types.sideshiftsTriple") },
      {
        value: 46,
        option: t("attachments.types.trilateralHeadsShaft"),
      },
      { value: 47, option: t("attachments.types.trilateralHeadsRack") },
      { value: 48, option: t("attachments.types.brickBlockClamps") },
      { value: 49, option: t("attachments.types.tobaccoClamps") },
      { value: 50, option: t("attachments.types.tissueRollClamps360") },
      { value: 51, option: t("attachments.types.carpetBoom") },
      { value: 52, option: t("attachments.types.loadExtenders") },
      { value: 53, option: t("attachments.types.pulpBaleClampsRigid") },
      {
        value: 54,
        option: t("attachments.types.pulpBaleClampsArticulating"),
      },
      { value: 55, option: t("attachments.types.forkPositioners360") },
      {
        value: 56,
        option: t("attachments.types.forkPositionersProfile"),
      },
      {
        value: 57,
        option: t("attachments.types.forkPositionersShaft"),
      },
      { value: 58, option: t("attachments.types.other") },
      { value: 59, option: t("attachments.types.snowPusher") },
      { value: 60, option: t("attachments.types.roundTimberGripper") },
      { value: 61, option: t("attachments.types.forkPositioners") },
      {
        value: 62,
        option: t("attachments.types.tineAdjustmentSideshift"),
      },
      { value: 63, option: t("attachments.types.pushOutForks") },
      { value: 64, option: t("attachments.types.baleTongsSideshift") },
      { value: 65, option: t("attachments.types.reelClampSwivelling") },
      {
        value: 66,
        option: t("attachments.types.tiltingDeviceShovels"),
      },
      {
        value: 67,
        option: t("attachments.types.doubleReelTurningClamping"),
      },
      { value: 68, option: t("attachments.types.attachmentClamp") },
      { value: 69, option: t("attachments.types.hydraulicRopeHoist") },
      { value: 70, option: t("attachments.types.telescopeForkTine") },
      { value: 71, option: t("attachments.types.accessories") },
      { value: 72, option: t("attachments.types.forks") },
      { value: 73, option: t("attachments.types.barrelLifter") },
      { value: 74, option: t("attachments.types.loadGuardGrill") },
      { value: 75, option: t("attachments.types.personsBasket") },
      { value: 76, option: t("attachments.types.telescopicCrane") },
      { value: 77, option: t("attachments.types.forkExtension") },
      { value: 78, option: t("attachments.types.pouchLifter") },
      { value: 79, option: t("attachments.types.sweepingMachine") },
      { value: 80, option: t("attachments.types.pipeClamp") },
      { value: 81, option: t("attachments.types.clampCementPipes") },
      { value: 82, option: t("attachments.types.containerSpreader") },
      { value: 83, option: t("attachments.types.piggybackSpreader") },
      { value: 84, option: t("attachments.types.liftTruckScales") },
    ];

    const attachment = attachmentTypes.find((type) => type.value == value);
    return attachment ? attachment.option : value;
  };

  // Add these helper functions around line 150 with your other helper functions:

  const getWorkPlatformClassification = (value) => {
    const classificationTypes = [
      { value: "", option: t("workPlatform.classificationTypes.select") },
      { value: 1, option: t("workPlatform.classificationTypes.other") },
      { value: 2, option: t("workPlatform.classificationTypes.trailed") },
      { value: 3, option: t("workPlatform.classificationTypes.telescope") },
      { value: 4, option: t("workPlatform.classificationTypes.vertical") },
      { value: 5, option: t("workPlatform.classificationTypes.articulated") },
      { value: 6, option: t("workPlatform.classificationTypes.scissors") },
      { value: 7, option: t("workPlatform.classificationTypes.truckMounted") },
      { value: 8, option: t("workPlatform.classificationTypes.special") },
      { value: 9, option: t("workPlatform.classificationTypes.spider") },
    ];
    const classification = classificationTypes.find(
      (type) => type.value == value
    );
    return classification ? classification.option : value;
  };

  const getWorkPlatformEngineType = (value) => {
    const engineTypes = [
      { value: "", option: t("workPlatform.placeholders.engineType") },
      { value: 1, option: t("assets.options.diesel") },
      { value: 2, option: t("assets.options.electric") },
      { value: 3, option: t("assets.options.lpg") },
      { value: 4, option: t("assets.options.gasoline-gas") },
      { value: 5, option: t("assets.options.hybrid") },
      { value: 6, option: t("assets.options.fuel-cell") },
      { value: 7, option: t("assets.options.lithium-ion") },
      { value: 8, option: t("assets.options.hand") },
    ];
    const engineType = engineTypes.find((type) => type.value == value);
    return engineType ? engineType.option : value;
  };

  // Add these helper functions around line 150 with your other helper functions:

  const getCleaningEquipmentClassification = (value) => {
    const classificationTypes = [
      { value: "", option: t("cleaningEquipment.classificationTypes.select") },
      { value: 1, option: t("cleaningEquipment.classificationTypes.other") },
      {
        value: 2,
        option: t("cleaningEquipment.classificationTypes.pedestrianSweeper"),
      },
      {
        value: 3,
        option: t(
          "cleaningEquipment.classificationTypes.pedestrianVacuumSweeper"
        ),
      },
      {
        value: 4,
        option: t(
          "cleaningEquipment.classificationTypes.pedestrianScrubberDryer"
        ),
      },
      {
        value: 5,
        option: t(
          "cleaningEquipment.classificationTypes.streetCleaningMachine"
        ),
      },
      {
        value: 6,
        option: t("cleaningEquipment.classificationTypes.pressureWasher"),
      },
      {
        value: 7,
        option: t(
          "cleaningEquipment.classificationTypes.pedestrianScrubberDryer2"
        ),
      },
      {
        value: 8,
        option: t("cleaningEquipment.classificationTypes.rideOnScrubberDryer"),
      },
      {
        value: 9,
        option: t("cleaningEquipment.classificationTypes.rideOnSweeper"),
      },
      {
        value: 10,
        option: t("cleaningEquipment.classificationTypes.rideOnVacuumSweeper"),
      },
      {
        value: 11,
        option: t("cleaningEquipment.classificationTypes.steamMachine"),
      },
      {
        value: 12,
        option: t("cleaningEquipment.classificationTypes.vacuumCleaner"),
      },
      {
        value: 13,
        option: t("cleaningEquipment.classificationTypes.escalatorCleaner"),
      },
      {
        value: 14,
        option: t("cleaningEquipment.classificationTypes.singleDiscMachine"),
      },
      {
        value: 15,
        option: t("cleaningEquipment.classificationTypes.carpetCleaner"),
      },
      {
        value: 16,
        option: t("cleaningEquipment.classificationTypes.ridingScrubberDryer"),
      },
    ];
    const classification = classificationTypes.find(
      (type) => type.value == value
    );
    return classification ? classification.option : value;
  };

  // Add this helper function around line 150:

  const getSparePartClassification = (value) => {
    const classificationTypes = [
      { value: "", option: t("spareParts.classificationTypes.select") },
      {
        value: "batteryindicator",
        option: t("spareParts.classificationTypes.batteryindicator"),
      },
      { value: "board", option: t("spareParts.classificationTypes.board") },
      { value: "cable", option: t("spareParts.classificationTypes.cable") },
      { value: "cap", option: t("spareParts.classificationTypes.cap") },
      {
        value: "chassispart",
        option: t("spareParts.classificationTypes.chassispart"),
      },
      { value: "control", option: t("spareParts.classificationTypes.control") },
      { value: "cover", option: t("spareParts.classificationTypes.cover") },
      {
        value: "cylinder",
        option: t("spareParts.classificationTypes.cylinder"),
      },
      {
        value: "driveengine",
        option: t("spareParts.classificationTypes.driveengine"),
      },
      {
        value: "drivetrain",
        option: t("spareParts.classificationTypes.drivetrain"),
      },
      { value: "harness", option: t("spareParts.classificationTypes.harness") },
      {
        value: "hydraulics",
        option: t("spareParts.classificationTypes.hydraulics"),
      },
      {
        value: "magneticbrake",
        option: t("spareParts.classificationTypes.magneticbrake"),
      },
      {
        value: "motorcontrol",
        option: t("spareParts.classificationTypes.motorcontrol"),
      },
      { value: "others", option: t("spareParts.classificationTypes.others") },
      {
        value: "powertrain",
        option: t("spareParts.classificationTypes.powertrain"),
      },
      {
        value: "pumpengine",
        option: t("spareParts.classificationTypes.pumpengine"),
      },
      { value: "relay", option: t("spareParts.classificationTypes.relay") },
      { value: "seat", option: t("spareParts.classificationTypes.seat") },
      {
        value: "steeringcolumn",
        option: t("spareParts.classificationTypes.steeringcolumn"),
      },
      {
        value: "steeringmotor",
        option: t("spareParts.classificationTypes.steeringmotor"),
      },
      {
        value: "supportwheel",
        option: t("spareParts.classificationTypes.supportwheel"),
      },
      { value: "tube", option: t("spareParts.classificationTypes.tube") },
      { value: "tyres", option: t("spareParts.classificationTypes.tyres") },
      {
        value: "valveblock",
        option: t("spareParts.classificationTypes.valveblock"),
      },
    ];
    const classification = classificationTypes.find(
      (type) => type.value === value
    );
    return classification ? classification.option : value;
  };

  const getCleaningEngineType = (value) => {
    const engineTypes = [
      { value: "", option: t("workPlatform.placeholders.engineType") },
      { value: 1, option: t("assets.options.diesel") },
      { value: 2, option: t("assets.options.electric") },
      { value: 3, option: t("assets.options.lpg") },
      { value: 4, option: t("assets.options.gasoline-gas") },
      { value: 5, option: t("assets.options.hybrid") },
      { value: 6, option: t("assets.options.fuel-cell") },
      { value: 7, option: t("assets.options.lithium-ion") },
      { value: 8, option: t("assets.options.hand") },
    ];
    const engineType = engineTypes.find((type) => type.value == value);
    return engineType ? engineType.option : value;
  };

  // Add these helper functions around line 150:

  const getConstructionMachineryClassification = (value) => {
    const classificationTypes = [
      {
        value: "",
        option: t("constructionMachinery.classificationTypes.select"),
      },
      {
        value: 2,
        option: t(
          "constructionMachinery.classificationTypes.hydraulicExcavators"
        ),
      },
      {
        value: 3,
        option: t("constructionMachinery.classificationTypes.wheelExcavator"),
      },
      {
        value: 4,
        option: t("constructionMachinery.classificationTypes.miniExcavator"),
      },
      {
        value: 5,
        option: t(
          "constructionMachinery.classificationTypes.demolitionExcavator"
        ),
      },
      {
        value: 6,
        option: t(
          "constructionMachinery.classificationTypes.rehandlingExcavator"
        ),
      },
      {
        value: 7,
        option: t(
          "constructionMachinery.classificationTypes.draglineExcavator"
        ),
      },
      {
        value: 8,
        option: t(
          "constructionMachinery.classificationTypes.roadRailExcavator"
        ),
      },
      {
        value: 10,
        option: t("constructionMachinery.classificationTypes.wheelLoader"),
      },
      {
        value: 11,
        option: t("constructionMachinery.classificationTypes.compactLoader"),
      },
      {
        value: 12,
        option: t(
          "constructionMachinery.classificationTypes.materialHandlingLoader"
        ),
      },
      {
        value: 14,
        option: t(
          "constructionMachinery.classificationTypes.rigidBackhoeLoader"
        ),
      },
      {
        value: 15,
        option: t(
          "constructionMachinery.classificationTypes.articulatedBackhoeLoader"
        ),
      },
      {
        value: 16,
        option: t(
          "constructionMachinery.classificationTypes.miniBackhoeLoader"
        ),
      },
      {
        value: 18,
        option: t("constructionMachinery.classificationTypes.bulldozer"),
      },
      {
        value: 19,
        option: t("constructionMachinery.classificationTypes.crawlerLoader"),
      },
      {
        value: 20,
        option: t(
          "constructionMachinery.classificationTypes.pipelayingCaterpillar"
        ),
      },
      {
        value: 21,
        option: t("constructionMachinery.classificationTypes.weldBead"),
      },
      {
        value: 23,
        option: t("constructionMachinery.classificationTypes.grader"),
      },
      {
        value: 24,
        option: t(
          "constructionMachinery.classificationTypes.selfPoweredScraper"
        ),
      },
      {
        value: 25,
        option: t("constructionMachinery.classificationTypes.trailedScraper"),
      },
      {
        value: 26,
        option: t("constructionMachinery.classificationTypes.scrapedozer"),
      },
      {
        value: 28,
        option: t(
          "constructionMachinery.classificationTypes.articulatedDumpTruck"
        ),
      },
      {
        value: 29,
        option: t("constructionMachinery.classificationTypes.rigidDumpTruck"),
      },
      {
        value: 30,
        option: t("constructionMachinery.classificationTypes.miniDumper"),
      },
      {
        value: 31,
        option: t("constructionMachinery.classificationTypes.trackedDumper"),
      },
      {
        value: 33,
        option: t("constructionMachinery.classificationTypes.mobileCrane"),
      },
      {
        value: 34,
        option: t("constructionMachinery.classificationTypes.truckCrane"),
      },
      {
        value: 35,
        option: t("constructionMachinery.classificationTypes.crawlerCrane"),
      },
      {
        value: 36,
        option: t("constructionMachinery.classificationTypes.towerCrane"),
      },
      {
        value: 37,
        option: t(
          "constructionMachinery.classificationTypes.selfErectingCrane"
        ),
      },
      {
        value: 38,
        option: t(
          "constructionMachinery.classificationTypes.mobileConstructionCrane"
        ),
      },
      {
        value: 39,
        option: t("constructionMachinery.classificationTypes.docksideCrane"),
      },
      {
        value: 40,
        option: t("constructionMachinery.classificationTypes.miniCrane"),
      },
      {
        value: 41,
        option: t("constructionMachinery.classificationTypes.auxiliaryCrane"),
      },
      {
        value: 43,
        option: t(
          "constructionMachinery.classificationTypes.singleDrumCompactor"
        ),
      },
      {
        value: 44,
        option: t(
          "constructionMachinery.classificationTypes.asphaltCompactorTandem"
        ),
      },
      {
        value: 45,
        option: t("constructionMachinery.classificationTypes.combiRoller"),
      },
      {
        value: 46,
        option: t(
          "constructionMachinery.classificationTypes.pneumaticCompactor"
        ),
      },
      {
        value: 47,
        option: t("constructionMachinery.classificationTypes.sheepsfootRoller"),
      },
      {
        value: 48,
        option: t(
          "constructionMachinery.classificationTypes.landfillCompactor"
        ),
      },
      {
        value: 49,
        option: t("constructionMachinery.classificationTypes.towedRoller"),
      },
      {
        value: 51,
        option: t("constructionMachinery.classificationTypes.truckMixer"),
      },
      {
        value: 52,
        option: t(
          "constructionMachinery.classificationTypes.concreteScrewConveyor"
        ),
      },
      {
        value: 53,
        option: t(
          "constructionMachinery.classificationTypes.truckConcretePump"
        ),
      },
      {
        value: 54,
        option: t("constructionMachinery.classificationTypes.concretePump"),
      },
      {
        value: 56,
        option: t("constructionMachinery.classificationTypes.wheeledPaver"),
      },
      {
        value: 57,
        option: t("constructionMachinery.classificationTypes.trackedPaver"),
      },
      {
        value: 58,
        option: t("constructionMachinery.classificationTypes.asphaltPlaner"),
      },
      {
        value: 59,
        option: t("constructionMachinery.classificationTypes.surfaceMiner"),
      },
      {
        value: 60,
        option: t("constructionMachinery.classificationTypes.asphaltRecycler"),
      },
      {
        value: 61,
        option: t("constructionMachinery.classificationTypes.soilStabilizer"),
      },
      {
        value: 62,
        option: t("constructionMachinery.classificationTypes.coldRecycler"),
      },
      {
        value: 63,
        option: t("constructionMachinery.classificationTypes.hotRecycler"),
      },
      {
        value: 64,
        option: t("constructionMachinery.classificationTypes.slipformPaver"),
      },
      {
        value: 65,
        option: t("constructionMachinery.classificationTypes.gritter"),
      },
      {
        value: 67,
        option: t("constructionMachinery.classificationTypes.drillingMachine"),
      },
      {
        value: 68,
        option: t("constructionMachinery.classificationTypes.anchorDrillRig"),
      },
      {
        value: 69,
        option: t("constructionMachinery.classificationTypes.drillRig"),
      },
      {
        value: 70,
        option: t("constructionMachinery.classificationTypes.blastholeDrill"),
      },
      {
        value: 71,
        option: t("constructionMachinery.classificationTypes.trenchCutter"),
      },
      {
        value: 72,
        option: t("constructionMachinery.classificationTypes.diphragmGrabWall"),
      },
      {
        value: 73,
        option: t("constructionMachinery.classificationTypes.depthVibrator"),
      },
      {
        value: 74,
        option: t("constructionMachinery.classificationTypes.chainTrencher"),
      },
      {
        value: 76,
        option: t(
          "constructionMachinery.classificationTypes.universalPillingRig"
        ),
      },
      {
        value: 77,
        option: t("constructionMachinery.classificationTypes.pilingHammer"),
      },
      {
        value: 79,
        option: t("constructionMachinery.classificationTypes.drilling"),
      },
      {
        value: 80,
        option: t("constructionMachinery.classificationTypes.tunnelDumper"),
      },
      {
        value: 81,
        option: t("constructionMachinery.classificationTypes.tunnelLoader"),
      },
      {
        value: 82,
        option: t("constructionMachinery.classificationTypes.tunnelExcavator"),
      },
      {
        value: 83,
        option: t(
          "constructionMachinery.classificationTypes.sprayConcretePump"
        ),
      },
      {
        value: 84,
        option: t("constructionMachinery.classificationTypes.tunnelTruckMixer"),
      },
      {
        value: 85,
        option: t("constructionMachinery.classificationTypes.tunnelingShield"),
      },
      {
        value: 86,
        option: t("constructionMachinery.classificationTypes.roadheader"),
      },
      {
        value: 88,
        option: t("constructionMachinery.classificationTypes.miningExcavator"),
      },
      {
        value: 89,
        option: t("constructionMachinery.classificationTypes.miningDumper"),
      },
      {
        value: 91,
        option: t("constructionMachinery.classificationTypes.others"),
      },
      {
        value: 93,
        option: t("constructionMachinery.classificationTypes.attachments"),
      },
      {
        value: 94,
        option: t("constructionMachinery.classificationTypes.powerGenerator"),
      },
      {
        value: 95,
        option: t("constructionMachinery.classificationTypes.pileDriving"),
      },
      {
        value: 96,
        option: t("constructionMachinery.classificationTypes.compressor"),
      },
      {
        value: 98,
        option: t("constructionMachinery.classificationTypes.trailer"),
      },
      {
        value: 100,
        option: t("constructionMachinery.classificationTypes.truck"),
      },
      {
        value: 101,
        option: t("constructionMachinery.classificationTypes.plateCompactor"),
      },
      {
        value: 102,
        option: t("constructionMachinery.classificationTypes.ramps"),
      },
    ];
    const classification = classificationTypes.find(
      (type) => type.value == value
    );
    return classification ? classification.option : value;
  };

  const getConstructionEngineType = (value) => {
    const engineTypes = [
      { value: "", option: t("workPlatform.placeholders.engineType") },
      { value: 1, option: t("assets.options.diesel") },
      { value: 2, option: t("assets.options.electric") },
      { value: 3, option: t("assets.options.lpg") },
      { value: 4, option: t("assets.options.gasoline-gas") },
      { value: 5, option: t("assets.options.hybrid") },
      { value: 6, option: t("assets.options.fuel-cell") },
      { value: 7, option: t("assets.options.lithium-ion") },
      { value: 8, option: t("assets.options.hand") },
    ];
    const engineType = engineTypes.find((type) => type.value == value);
    return engineType ? engineType.option : value;
  };

  const statusOptions = [
    { value: "New", option: t("assets.status.New") },
    { value: "Used", option: t("assets.status.Used") },
    { value: "Short Time Hire", option: t("assets.status.shortTermHire") },
    { value: "Demo", option: t("assets.status.Demo") },
  ];

  const hasSecondColumnData = () => {
    return (
      selectedAsset?.rear_tire_size ||
      selectedAsset?.book_value ||
      selectedAsset?.battery_type ||
      selectedAsset?.battery_manufacturer ||
      selectedAsset?.batterycapacity ||
      selectedAsset?.batterylength ||
      selectedAsset?.battery_location ||
      selectedAsset?.batterymodel ||
      selectedAsset?.battery_voltage ||
      selectedAsset?.batteryweight ||
      selectedAsset?.batterywidth ||
      selectedAsset?.chargerLoadertype ||
      selectedAsset?.chargerManufacturer ||
      selectedAsset?.chargerAC ||
      selectedAsset?.chargerCurrent ||
      selectedAsset?.chargerModel ||
      selectedAsset?.chargerVoltage ||
      (selectedAsset?.additional_equipment &&
        Array.isArray(selectedAsset?.additional_equipment) &&
        selectedAsset.additional_equipment.length > 0) ||
      selectedAsset?.end_user_price
    );
  };

  const getConditionValue = (value) => {
    const conditionValue = conditions.find(
      (condition) => condition.value == value
    );
    return conditionValue ? conditionValue.option : "N/A";
  };

  const getChargerType = (value) => {
    const chargerType = chargerTypes.find((type) => type.value == value);
    return chargerType ? chargerType.option : "N/A";
  };

  const getTireType = (value) => {
    const tireType = tireTypes.find((type) => type.value == value);
    return tireType ? tireType.option : "N/A";
  };

  const getBatteryType = (value) => {
    const batteryType = batteryTypes.find((type) => type.value == value);
    return batteryType ? batteryType.option : "N/A";
  };

  function formatToEuropean(number) {
    return Number(number).toLocaleString("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  const shouldDisplayValue = (value) => {
    return (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      value !== 0 &&
      value !== "0" &&
      value !== false
    );
  };

  const ADD_EQUIPMENT_OPTIONS = [
    "3rd valve",
    "4th valve",
    "Spotlight rear",
    "Spotlight front",
    "Overhead Guard",
    "Front pane",
    "Weatherproof cover",
    "Heater",
    "Load guard grill",
    "Solenoid valve",
    "Oil separator",
    "Diesel Particle Filter",
    "Road traffic regulation",
    "Weatherproof cabin",
    "Explosion-proof",
    "Pulse control",
    "Initial stroke",
    "Air-condition",
    "Full free-lift",
    "CE certificate",
    "Special Trucks",
    "Stainless Steel",
    "Cold Store",
    "Scales",
    "Twin Tires",
    "Safety Light",
    "Safety Pilot",
    "Non-marking Tires",
    "Lithium-Ion technology",
    "Double-deck loading",
    "Self-driving",
    "Rear view mirror",
    "Side mirror(s)",
    "Joystick",
    "Beacon light",
    "Wipers",
    "Single pedal",
    "Heated seat",
    "LED",
    "Deep Reach",
    "Wire Guidance",
    "Galvanized",
    "3-wheel",
    "Seat",
  ];

  const formatKey = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getTranslatedFieldName = (key) => {
    return t(
      `assetUpdate.fields.${key}`,
      t(`viewAsset.fields.${key}`, formatKey(key))
    );
  };

  function formatKeyToLabel(key) {
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → "camel Case"
      .replace(/_/g, " ") // snake_case → "snake case"
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize words
  }

  const handleViewAsset = async () => {
    setShowModal(true);
    setLoading(true);

    try {
      // Add language parameter to the query
      const query = `language=${currentLanguages}`;
      const response = await getProtected(
        `${API_END_POINTS.assets}${id}/?${query}`
      );

      if (response && !response.error) {
        console.log("detail view asset response = ", response);
        setSelectedAsset(response);
        setImageAttachments(getImageAttachments(response) ?? []);
        // await getAllMaintainance();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      console.error("Error fetching asset details:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    if (!contentRef.current || exportingPDF) return;

    setExportingPDF(true);

    try {
      const originalElement = contentRef.current;

      const clonedElement = originalElement.cloneNode(true);

      const elementsToRemove = clonedElement.querySelectorAll(".no-pdf");
      elementsToRemove.forEach((el) => el.remove());
      const remarkInput = clonedElement.querySelector("#remark-input");
      const remarkSection = clonedElement.querySelector("#remark-section");
      if (remarkSection) {
        if (!remark.trim()) {
          remarkSection.style.display = "none";
        } else {
          // Replace input with plain text for PDF
          if (remarkInput) {
            const textSpan = document.createElement("span");
            textSpan.textContent = remark;
            textSpan.style.display = "block";
            textSpan.style.padding = "8px 0";
            textSpan.style.color = "#222";
            textSpan.style.fontWeight = "bold";
            remarkInput.parentNode.replaceChild(textSpan, remarkInput);
          }
        }
      }

      clonedElement.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: ${originalElement.offsetWidth}px;
        background: white !important;
        padding: 20px !important;
        margin: 0 !important;
        transform: scale(1) !important;
        z-index: -1;
      `;

      document.body.appendChild(clonedElement);

      clonedElement.querySelectorAll("[data-pdf-text-block]").forEach((block) => {
        block.style.width = "100%";
        block.style.display = "grid";
        block.style.gridTemplateColumns = "1fr 1fr";
        block.style.gap = "8px 16px";

        const valueEl = block.querySelector("span:last-child");
        if (valueEl) {
          valueEl.style.minWidth = "0";
          valueEl.style.width = "100%";
          valueEl.style.maxWidth = "100%";
          valueEl.style.textAlign = "right";
          valueEl.style.wordBreak = "break-word";
          valueEl.style.overflowWrap = "break-word";
          valueEl.style.whiteSpace = "normal";
          valueEl.style.lineHeight = "1.5";
        }
      });

      await buildPdfImageGallery(clonedElement, imageAttachments);
      await inlineLocalImagesForPdf(clonedElement);
      await new Promise((resolve) => setTimeout(resolve, 200));

      const canvas = await html2canvas(clonedElement, {
        scale: 2, // Increased scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: clonedElement.scrollWidth,
        height: clonedElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      document.body.removeChild(clonedElement);

      const imgData = canvas.toDataURL("image/jpeg", 1.0); // Changed to JPEG with max quality

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // Add margins

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pageWidth - margin * 2;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      // Check if content fits on one page
      if (pdfHeight <= pageHeight - margin * 2) {
        // Single page
        pdf.addImage(imgData, "JPEG", margin, margin, pdfWidth, pdfHeight);
      } else {
        // Multiple pages needed
        const pageContentHeight = pageHeight - margin * 2;
        const totalPages = Math.ceil(pdfHeight / pageContentHeight);

        for (let i = 0; i < totalPages; i++) {
          if (i > 0) {
            pdf.addPage();
          }

          const sourceY = (i * pageContentHeight * imgProps.height) / pdfHeight;
          const sourceHeight = Math.min(
            (pageContentHeight * imgProps.height) / pdfHeight,
            imgProps.height - sourceY
          );

          const destHeight = (sourceHeight * pdfHeight) / imgProps.height;

          pdf.addImage(
            imgData,
            "JPEG",
            margin,
            margin,
            pdfWidth,
            destHeight,
            undefined,
            "FAST",
            0,
            -sourceY
          );
        }
      }

      const filename = `forklift-details-${
        selectedAsset?.manufacturer || "asset"
      }-${id || "report"}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setExportingPDF(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleViewAsset(id);
    }
  }, [id, currentLanguages]);

  // Handle document download
  const handleDownloadDocument = async (attachmentId, filename) => {
    try {
      const url = await getDownloadUrl(attachmentId);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download document:", error);
      toast.error("Failed to download document");
    }
  };

  const technicalSpecs = [
    {
      label: "Fabrikant",
      value: "Linde",
      labelEn: "Manufacturer",
      valueEn: "Linde",
    },
    {
      label: "Artikelnummer",
      value: "236.112",
      labelEn: "Article number",
      valueEn: "236.112",
    },
    { label: "Merk", value: "H30", labelEn: "Brand", valueEn: "H30" },
    { label: "Type", value: "Fourway", labelEn: "Type", valueEn: "Fourway" },
    { label: "Drager", value: "Diesel", labelEn: "Carrier", valueEn: "Diesel" },
    {
      label: "Hefcapaciteit",
      value: "3000 kg",
      labelEn: "Lifting capacity",
      valueEn: "3000 kg",
    },
    {
      label: "Eigengewicht",
      value: "6300 kg",
      labelEn: "Own weight",
      valueEn: "6300 kg",
    },
    {
      label: "Hefhoogte",
      value: "4.5 m",
      labelEn: "Lifting height",
      valueEn: "4.5 m",
    },
    {
      label: "Vrijhefhoogte",
      value: "1.5 m",
      labelEn: "Free lifting height",
      valueEn: "1.5 m",
    },
    { label: "Hoogte", value: "2.3 m", labelEn: "Height", valueEn: "2.3 m" },
    { label: "Lengte", value: "4.2 m", labelEn: "Length", valueEn: "4.2 m" },
    { label: "Breedte", value: "1.8 m", labelEn: "Width", valueEn: "1.8 m" },
  ];

  const filteredEntries = Object.entries(selectedAsset || {}).filter(
    ([key, value]) => {
      const excludedKeys = [
        "created_at",
        "updated_at",
        "id",
        "attachments",
        "contracts",
      ];
      const hasValidValue =
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== false &&
        value !== 0 &&
        value !== "N/A" &&
        value !== "n/a";
      return !excludedKeys.includes(key) && hasValidValue;
    }
  );

  const midPoint = Math.ceil(filteredEntries.length / 2);
  const dutchEntries = filteredEntries.slice(0, midPoint);
  const englishEntries = filteredEntries.slice(midPoint);

  const formatAttachment = (attachment) => {
    console.log("attachment = ", attachment);

    const fileName = attachment.split("/").pop();
    console.log("filename = ", fileName);
    return fileName;
  };

  const getTransportOrderHistory = (asset) => {
    if (!asset) return [];
    const history = asset.transport_order_history ?? asset.transport_orders;
    if (!Array.isArray(history)) return [];
    return history.filter((item) => item?.transport_order_url);
  };

  const formatTransportOrderLabel = (url) => {
    if (!url) return "";
    const path = url.split("?")[0];
    try {
      return decodeURIComponent(path.split("/").pop() || "transport-order.pdf");
    } catch {
      return path.split("/").pop() || "transport-order.pdf";
    }
  };

  const transportOrderHistory = getTransportOrderHistory(selectedAsset);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
        <div className="text-center px-4 w-full max-w-sm">
          <div className="mx-auto h-12 w-12 sm:h-14 sm:w-14 mb-4 sm:mb-6">
            <div className="animate-spin h-full w-full border-4 sm:border-8 border-gray-200 border-t-blue-500 rounded-full"></div>
          </div>
          <div className="text-white text-lg sm:text-xl font-medium break-words">
            {t("detailViewModel.loadingDetailView")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="bg-white min-h-screen">
      {/* Header with Export Button */}
      <HeroSection t={t} />
      <div className="bg-white p-4 sm:p-6 max-w-[90rem] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-center gap-4 lg:flex-[7]">
            <div className="text-xl sm:text-2xl font-bold text-gray-800 flex flex-wrap items-center justify-between w-full">
              <div className="flex flex-wrap">
                <span>{selectedAsset?.manufacturer || ""}</span>
                {selectedAsset?.type && (
                  <span>
                    {", "}
                    {selectedAsset?.type || ""}
                  </span>
                )}
                {/* {selectedAsset?.classification && (
                <span>
                  {", "}
                  {selectedAsset?.classification
                    ? selectedAsset?.classification
                    : ""}
                </span>
              )} */}
                {selectedAsset?.asset_type === "Forklifts" &&
                  selectedAsset?.classification && (
                    <span>
                      {", "}
                      {selectedAsset?.classification}
                    </span>
                  )}

                {/* Work Platform Classification */}
                {(selectedAsset?.asset_type === "Work Platforms" ||
                  selectedAsset?.asset_type === "WorkPlatform") &&
                  selectedAsset?.workplatform_toc && (
                    <span>
                      {", "}
                      {getWorkPlatformClassification(
                        selectedAsset?.workplatform_toc
                      )}
                    </span>
                  )}

                {/* Cleaning Equipment Classification */}
                {(selectedAsset?.asset_type === "Cleaning Equipment" ||
                  selectedAsset?.asset_type === "CleaningEquipment") &&
                  selectedAsset?.cleaning_toc && (
                    <span>
                      {", "}
                      {getCleaningEquipmentClassification(
                        selectedAsset?.cleaning_toc
                      )}
                    </span>
                  )}

                {/* Construction Machinery Classification */}
                {(selectedAsset?.asset_type === "Construction Machinery" ||
                  selectedAsset?.asset_type === "ConstructionMachinery") &&
                  selectedAsset?.construction_classification && (
                    <span>
                      {", "}
                      {getConstructionMachineryClassification(
                        selectedAsset?.construction_classification
                      )}
                    </span>
                  )}

                {/* Spare Parts Classification */}
                {(selectedAsset?.asset_type === "Spare Parts" ||
                  selectedAsset?.asset_type === "SpareParts") &&
                  selectedAsset?.sparetype && (
                    <span>
                      {", "}
                      {getSparePartClassification(selectedAsset?.sparetype)}
                    </span>
                  )}

                {/* Battery Type Classification */}
                {selectedAsset?.asset_type === "Batteries" &&
                  selectedAsset?.battery_type && (
                    <span>
                      {", "}
                      {getBatteryType(selectedAsset?.battery_type)}
                    </span>
                  )}

                {/* Charger Type Classification */}
                {selectedAsset?.asset_type === "Chargers" &&
                  selectedAsset?.chargerLoadertype && (
                    <span>
                      {", "}
                      {getChargerType(selectedAsset?.chargerLoadertype)}
                    </span>
                  )}

                {/* Lift Mast Classification */}
                {(selectedAsset?.asset_type === "Lift Masts" ||
                  selectedAsset?.asset_type === "LiftMast") &&
                  selectedAsset?.mast_type && (
                    <span>
                      {", "}
                      {getMastClassification(selectedAsset?.mast_type)}
                    </span>
                  )}

                {/* Attachments Classification */}
                {selectedAsset?.asset_type === "Attachments" &&
                  selectedAsset?.attachment_toc && (
                    <span>
                      {", "}
                      {getAttachmentsClassification(
                        selectedAsset?.attachment_toc
                      )}
                    </span>
                  )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-4 lg:flex-[3]">
            <div className="flex items-center justify-between sm:justify-end gap-4 order-2 sm:order-1">
              {/* Price/On Request moved to be inline with buttons */}
              <div className="flex flex-col items-end ml-auto">
                {selectedAsset?.end_user_price ? (
                  <div className="text-lg font-bold text-blue-600 text-right whitespace-nowrap">
                    {formatToEuropean(selectedAsset.end_user_price) + " €"}
                  </div>
                ) : (
                  <div className="text-lg font-bold text-blue-600 text-right whitespace-nowrap">
                    {t("assetManagerDashboardList.onRequest")}
                  </div>
                )}
                {selectedAsset?.book_value && (
                  <div className="text-sm font-bold text-blue-600 no-pdf">
                    ( BV : {formatToEuropean(selectedAsset?.book_value)} €)
                  </div>
                )}
              </div>
              {/* <div className="mt-2 sm:mt-0"> */}
              <button
                onClick={() => {
                  navigate("/dashboard");
                }}
                className="no-pdf text-gray-500 hover:text-gray-700 transition-colors p-1 self-start"
                aria-label="Close"
              >
                <X size={20} className="sm:w-6 sm:h-6" />
              </button>
              {/* </div> */}
            </div>

            <div className="flex items-center gap-2 sm:gap-3 order-1 sm:order-2">
              {/* PDF Export Button */}
              <button
                onClick={exportToPDF}
                disabled={exportingPDF}
                className={`no-pdf relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  exportingPDF
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                <Download size={16} />
                <span className="hidden sm:inline">
                  {exportingPDF
                    ? t("detailViewModel.exporting") + "..."
                    : t("detailViewModel.export")}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Content Area */}
      <div className="bg-white p-6 max-w-[90rem] mx-auto">
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
          {/* Left Side - Main Image */}
          <div className="flex flex-col" data-pdf-image-section>
            {/* Main Image */}
            {imageAttachments.length === 0 && (
              <div className="flex justify-center lg:justify-start mb-4">
                <img
                  src={defaultImage}
                  alt="Forklift view"
                  className="h-64 w-full sm:h-80 lg:h-[400px] object-cover rounded-lg"
                />
              </div>
            )}

            {imageAttachments.length === 1 && (
              <div className="flex justify-center lg:justify-start mb-4">
                <img
                  src={getAttachmentLargeUrl(imageAttachments[0])}
                  alt="Forklift view"
                  className="h-64 w-full sm:h-80 lg:h-[400px] object-contain hover:opacity-80 transition-opacity cursor-pointer rounded-lg bg-gray-50"
                />
              </div>
            )}

            {imageAttachments.length > 1 && (
              <div className="bg-gray-50 rounded-lg overflow-hidden h-64 sm:h-80 lg:h-[400px] w-full mb-4">
                <CarouselDetailView
                  attachments={imageAttachments}
                  loadedFullImageIndices={loadedFullImageIndices}
                  activeIndex={currentImageIndex}
                  onSlideChange={setCurrentImageIndex}
                />
              </div>
            )}

            {/* Thumbnail Carousel - Now below main image */}
            {imageAttachments.length > 1 && (
              <div className="mb-6 mt-2 flex items-center gap-1 no-pdf">
                {imageAttachments.length > thumbnailsVisible && (
                  <button
                    onClick={handlePrevThumbnails}
                    disabled={currentThumbnailStartIndex === 0}
                    className={`flex-shrink-0 p-1.5 rounded-full bg-white shadow-md border no-pdf transition-all duration-300 ${
                      currentThumbnailStartIndex === 0
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:scale-110 hover:shadow-lg hover:bg-blue-50"
                    }`}
                    aria-label="Previous thumbnails"
                  >
                    <ChevronLeft size={14} className="text-gray-700" />
                  </button>
                )}

                <div
                  className="overflow-hidden"
                  style={{ maxWidth: `${thumbnailsVisible * thumbnailStepRem}rem` }}
                >
                  <div
                    className="flex gap-2 transition-transform duration-500 ease-out"
                    style={{
                      transform: `translateX(-${
                        currentThumbnailStartIndex * thumbnailStepRem
                      }rem)`,
                    }}
                  >
                    {imageAttachments.map((attachment, index) => (
                      <div
                        key={attachment.id ?? index}
                        className="group relative flex-shrink-0"
                      >
                        <div
                          className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm border-2 transition-all duration-300 ${
                            currentImageIndex === index
                              ? "border-blue-500 ring-2 ring-blue-300"
                              : "border-gray-200"
                          }`}
                        >
                          <img
                            src={getAttachmentThumbUrl(attachment)}
                            alt={`Forklift view ${index + 1}`}
                            onClick={() => handleThumbnailClick(index)}
                            className="w-full h-full object-cover transition-all duration-300 ease-out group-hover:scale-105 cursor-pointer"
                          />
                        </div>
                        <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-300 pointer-events-none"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {imageAttachments.length > thumbnailsVisible && (
                  <button
                    onClick={handleNextThumbnails}
                    disabled={
                      currentThumbnailStartIndex + thumbnailsVisible >=
                      imageAttachments.length
                    }
                    className={`flex-shrink-0 p-1.5 rounded-full bg-white shadow-md border no-pdf transition-all duration-300 ${
                      currentThumbnailStartIndex + thumbnailsVisible >=
                      imageAttachments.length
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:scale-110 hover:shadow-lg hover:bg-blue-50"
                    }`}
                    aria-label="Next thumbnails"
                  >
                    <ChevronRight size={14} className="text-gray-700" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right Side - Contact Provider Section */}
          <div className="flex flex-col justify-start">
            {/* Contact Provider Section */}
            <div className="bg-gray-600 text-white p-2 mb-2 text-center">
              <h2 className="text-sm font-bold uppercase tracking-wide">
                Contact Provider
              </h2>
            </div>

            <div className="bg-gray-50 border border-gray-300 p-4 rounded-none flex flex-col gap-3 relative h-fit">
              {/* Edit/Save icon at top-right */}
              {/* Logo Section */}
              <div className="mb-6 w-full">
                <div className="flex justify-start">
                  <img
                    src={logo}
                    alt="Company Logo or Header"
                    className="h-auto object-contain"
                    style={{
                      maxHeight: "60px",
                      maxWidth: "180px",
                    }}
                  />
                </div>
              </div>
              <div className="absolute top-4 right-4 no-pdf">
                {!isEditingContact ? (
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-800"
                    onClick={() => setIsEditingContact(true)}
                    aria-label="Edit Contact"
                  >
                    <Edit size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-green-600 hover:text-green-800"
                    onClick={() => setIsEditingContact(false)}
                    aria-label="Save Contact"
                  >
                    <Save size={18} />
                  </button>
                )}
              </div>

              {/* Contact Details remain the same */}
              {/* Company Name */}
              <div>
                {isEditingContact ? (
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="text-gray-800 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="Logisnext Germany"
                  />
                ) : (
                  <span className="text-gray-800 font-bold">
                    {contactName || "Logisnext Germany"}
                  </span>
                )}
              </div>

              {/* Address */}
              <div>
                {/* <span className="font-semibold text-gray-700">Address: </span> */}
                {isEditingContact ? (
                  <input
                    type="text"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="text-gray-800 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="Lessingstraße 14, 46149 Oberhausen, Germany"
                  />
                ) : (
                  <span className="text-gray-800">
                    {contactEmail ||
                      "Lessingstraße 14, 46149 Oberhausen, Germany"}
                  </span>
                )}
              </div>

              {/* Website */}
              <div>
                {/* <span className="font-semibold text-gray-700">Website: </span> */}
                {isEditingContact ? (
                  <input
                    type="text"
                    value={contactWebsite}
                    onChange={(e) => setContactWebsite(e.target.value)}
                    className="text-gray-800 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    placeholder="https://www.mitsubishi-forklift.de"
                  />
                ) : (
                  <a
                    href={
                      contactWebsite || "https://www.mitsubishi-forklift.de"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {contactWebsite || "https://www.mitsubishi-forklift.de"}
                  </a>
                )}
              </div>
              {/* Name */}
              <div>
                {/* <span className="font-semibold text-gray-700">Name: </span> */}
                <span className="text-gray-800 font-bold">
                  {localStorage.getItem("name") || "N/A"}
                </span>
              </div>
              {/* Phone */}
              <div>
                {/* <span className="font-semibold text-gray-700">Phone: </span> */}
                {isEditingContact ? (
                  <div className="inline-flex items-center border-b border-gray-300 focus-within:border-blue-500">
                    <input
                      type="text"
                      value={contactPhoneCountry || "+49"}
                      onChange={(e) => {
                        let val = e.target.value.replace(/[^+\d]/g, "");
                        if (!val.startsWith("+"))
                          val = "+" + val.replace(/^\+*/, "");
                        setContactPhoneCountry(val.slice(0, 4));
                      }}
                      className="w-16 text-gray-800 bg-transparent border-none focus:outline-none focus:ring-0"
                      placeholder="+49"
                      maxLength={4}
                    />
                    <input
                      type="tel"
                      value={contactPhoneNumber}
                      onChange={(e) => {
                        const numbersOnly = e.target.value.replace(/\D/g, "");
                        setContactPhoneNumber(numbersOnly);
                      }}
                      className="flex-1 text-gray-800 bg-transparent border-none focus:outline-none focus:ring-0 min-w-0"
                      placeholder="20899999"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      maxLength={15}
                    />
                  </div>
                ) : (
                  <span className="text-gray-800">
                    {(contactPhoneCountry || "+49") +
                      " " +
                      (contactPhoneNumber || "20899999")}
                  </span>
                )}
              </div>

              {/* Email */}
              <div>
                {/* <span className="font-semibold text-gray-700">Email: </span> */}
                <span className="text-gray-800">
                  {localStorage.getItem("userEmail") || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {selectedAsset?.attachments &&
          Array.isArray(selectedAsset.attachments) &&
          selectedAsset.attachments.length > 0 && (
            <div className="mb-8 w-full no-pdf ">
              <h2 className="text-lg font-semibold mb-4 handwriting text-gray-700">
                {t("detailViewModel.attachments")}
              </h2>

              <div className=" gap-8 w-full ">
                {selectedAsset.attachments.map((item, index) => {
                  if (item?.document) {
                    return (
                      <div
                        key={index}
                        className="bg-gray-50 p-4 rounded-lg flex justify-between items-center hover:bg-gray-100 w-full  transition"
                      >
                        <span className="text-sm font-medium text-gray-800 break-all">
                          {formatAttachment(item.document)}
                        </span>
                        <button
                          onClick={() => handleDownloadDocument(item.id, formatAttachment(item.document))}
                          className="text-blue-600 text-sm font-semibold underline ml-4 cursor-pointer"
                        >
                          {t("detailViewModel.view")}
                        </button>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

        {transportOrderHistory.length > 0 && (
          <div className="mb-8 w-full no-pdf">
            <h2 className="text-lg font-semibold mb-4 handwriting text-gray-700">
              {t("detailViewModel.transportOrders")}
            </h2>

            <div className="gap-8 w-full">
              {transportOrderHistory.map((item, index) => {
                const orderUrl = resolveMediaUrl(item.transport_order_url);
                return (
                  <div
                    key={`${item.uploaded_at || index}-${item.transport_order_url}`}
                    className="bg-gray-50 p-4 rounded-lg flex justify-between items-center hover:bg-gray-100 w-full transition mb-2"
                  >
                    <div className="flex flex-col min-w-0 pr-4">
                      <span className="text-sm font-medium text-gray-800 break-all">
                        {formatTransportOrderLabel(item.transport_order_url)}
                      </span>
                      {item.uploaded_at && (
                        <span className="text-xs text-gray-500 mt-1">
                          {t("detailViewModel.uploadedAt")}:{" "}
                          {new Date(item.uploaded_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <a
                      href={orderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm font-semibold underline ml-4 shrink-0"
                    >
                      {t("detailViewModel.view")}
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Technical Specifications */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-lg font-semibold mb-3 sm:mb-4 handwriting bg-gray-600 text-white p-1">
            {t("detailViewModel.detailedSpecification")}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* First Specifications Column */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <h3 className="font-semibold mb-2 sm:mb-3 text-gray-800">
                {t("detailViewModel.technicalSpecification")}
              </h3>
              <div className="space-y-1 sm:space-y-2">
                {shouldDisplayValue(selectedAsset?.operating_hours) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.operatingHours")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.operating_hours} h
                    </span>
                  </div>
                )}

                {selectedAsset?.manufacturer &&
                  selectedAsset.manufacturer !== "" && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600 self-start">
                        {t("assets.fields.manufacturer")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right break-words whitespace-normal leading-relaxed min-w-0">
                        {selectedAsset?.manufacturer}
                      </span>
                    </div>
                  )}

                {selectedAsset?.type && selectedAsset.type !== "" && (
                  <div
                    className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1"
                    data-pdf-text-block
                  >
                    <span className="text-xs sm:text-sm text-gray-600 self-start">
                      {t("assets.fields.type")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right break-words whitespace-normal leading-relaxed min-w-0">
                      {selectedAsset?.type}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.description) && (
                  <div
                    className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1"
                    data-pdf-text-block
                  >
                    <span className="text-xs sm:text-sm text-gray-600 self-start">
                      {t("assets.fields.description")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right break-words whitespace-normal leading-relaxed min-w-0">
                      {selectedAsset?.description}
                    </span>
                  </div>
                )}

                {formatYearOfManufacture(selectedAsset?.year_of_manufacture) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.yearOfManufacture")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {formatYearOfManufacture(selectedAsset?.year_of_manufacture)}
                    </span>
                  </div>
                )}

                {selectedAsset?.serial_no && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">{`${t(
                      "assets.fields.machineSerialNo"
                    )} `}</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.serial_no}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.capacity) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.capacity")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.capacity} kg
                    </span>
                  </div>
                )}

                {selectedAsset?.dealer_street && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("detailView.warehouseStreet")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.dealer_street}
                    </span>
                  </div>
                )}

                {selectedAsset?.dealer_city && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("detailView.warehouseCity")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.dealer_city}
                    </span>
                  </div>
                )}

                {selectedAsset?.dealer_country && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("detailView.warehouseCountry")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.dealer_country}
                    </span>
                  </div>
                )}

                {selectedAsset?.dealer_zip && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("detailView.warehouseZip")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.dealer_zip}
                    </span>
                  </div>
                )}

                {selectedAsset?.asset_type === "Forklifts" &&
                  selectedAsset?.classification && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("assets.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.classification}
                      </span>
                    </div>
                  )}
                {(selectedAsset?.asset_type === "Work Platforms" ||
                  selectedAsset?.asset_type === "WorkPlatform") &&
                  shouldDisplayValue(selectedAsset?.workplatform_toc) && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("workPlatform.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getWorkPlatformClassification(
                          selectedAsset?.workplatform_toc
                        )}
                      </span>
                    </div>
                  )}

                {/* Cleaning Equipment Classification */}
                {(selectedAsset?.asset_type === "Cleaning Equipment" ||
                  selectedAsset?.asset_type === "CleaningEquipment") &&
                  shouldDisplayValue(selectedAsset?.cleaning_toc) && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("cleaningEquipment.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getCleaningEquipmentClassification(
                          selectedAsset?.cleaning_toc
                        )}
                      </span>
                    </div>
                  )}

                {/* Construction Machinery Classification */}
                {/* {(selectedAsset?.asset_type === "Construction Machinery" ||
                  selectedAsset?.asset_type === "ConstructionMachinery") &&
                  shouldDisplayValue(
                    selectedAsset?.construction_classification
                  ) && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("constructionMachinery.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getConstructionMachineryClassification(
                          selectedAsset?.construction_classification
                        )}
                      </span>
                    </div>
                  )} */}

                {/* Spare Parts Classification */}
                {(selectedAsset?.asset_type === "Spare Parts" ||
                  selectedAsset?.asset_type === "SpareParts") &&
                  shouldDisplayValue(selectedAsset?.sparetype) && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("assets.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getSparePartClassification(selectedAsset?.sparetype)}
                      </span>
                    </div>
                  )}

                {/* Battery Type Classification */}
                {selectedAsset?.asset_type === "Batteries" &&
                  selectedAsset?.battery_type && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("createAsset.placeholders.batteryType")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getBatteryType(selectedAsset?.battery_type)}
                      </span>
                    </div>
                  )}

                {/* Charger Type Classification */}
                {selectedAsset?.asset_type === "Chargers" &&
                  selectedAsset?.chargerLoadertype && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("assets.options.chargerType")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getChargerType(selectedAsset?.chargerLoadertype)}
                      </span>
                    </div>
                  )}

                {/* Lift Mast Classification */}
                {(selectedAsset?.asset_type === "Lift Mast" ||
                  selectedAsset?.asset_type === "LiftMast") &&
                  shouldDisplayValue(
                    selectedAsset?.liftmast_classification
                  ) && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("assets.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getMastClassification(
                          selectedAsset?.liftmast_classification
                        )}
                      </span>
                    </div>
                  )}

                {/* Attachments Classification - Add this helper function first if not already present */}
                {selectedAsset?.asset_type === "Attachments" &&
                  shouldDisplayValue(selectedAsset?.attachment_toc) && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("attachments.fields.classification")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getAttachmentsClassification(
                          selectedAsset?.attachment_toc
                        )}
                      </span>
                    </div>
                  )}

                {selectedAsset?.asset_type && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("detailView.assetType")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.asset_type}
                    </span>
                  </div>
                )}

                {selectedAsset?.status && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">{`${t(
                      "assets.fields.status"
                    )}`}</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {statusOptions.find(
                        (opt) => opt.value === selectedAsset.status
                      )?.option || selectedAsset.status}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.intern_no) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.intern_no}
                    </span>
                  </div>
                )}

                {/* {selectedAsset?.engine_type && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t(`engineTypes.${selectedAsset.engine_type}`)}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.engine_type}
                    </span>
                  </div>
                )} */}
                {selectedAsset?.engine_type && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.engineType")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {engineTypeOptions.find(
                        (opt) => opt.value === selectedAsset.engine_type
                      )?.option || selectedAsset.engine_type}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.closed_height_mm) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.closedHeight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.closed_height_mm} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.machine_length_mm) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      Machine lenght (mm)
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.machine_length_mm} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.machine_width_mm) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.machineWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.machine_width_mm} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.machine_height_mm) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.machineHeight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.machine_height_mm} mm
                    </span>
                  </div>
                )}

                {selectedAsset?.technical_condition && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.technicalConditionLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getConditionValue(selectedAsset?.technical_condition)}
                    </span>
                  </div>
                )}

                {selectedAsset?.optical_condition && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.opticalConditionLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getConditionValue(selectedAsset?.optical_condition)}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.free_height) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.forkLengthLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.free_height} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.forks_description) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.forks")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right break-words hyphens-auto leading-relaxed">
                      {selectedAsset?.forks_description}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.tire_type) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.tireType")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getTireType(selectedAsset?.tire_type)}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.front_tires) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.frontTires")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.front_tires}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.front_tire_size) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.frontTireSize")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.front_tire_size}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.rear_tires) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.rearTires")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.rear_tires}
                    </span>
                  </div>
                )}

                {/* Work Platform specific fields */}
                {shouldDisplayValue(selectedAsset?.workplatform_toc) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getWorkPlatformClassification(
                        selectedAsset?.workplatform_toc
                      )}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_internalno) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_internalno}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_enginetype) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.engineTypeLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getWorkPlatformEngineType(
                        selectedAsset?.workplatform_enginetype
                      )}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_basketload) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.basketLoad")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_basketload} kg
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_battery) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.battery")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_battery}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_height) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.height")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_height} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_length) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.length")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_length} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_weight) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.weight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_weight} kg
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_width) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.width")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_width} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.workplatform_workheight) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.workingHeight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.workplatform_workheight} mm
                    </span>
                  </div>
                )}
                {shouldDisplayValue(selectedAsset?.workplatform_available) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(
                        selectedAsset?.workplatform_available
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* {shouldDisplayValue(selectedAsset?.workplatform_available) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("workPlatform.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(selectedAsset?.workplatform_available).toLocaleDateString()}
                    </span>
                  </div>
                )} */}

                {/* Cleaning Equipment specific fields */}
                {shouldDisplayValue(selectedAsset?.cleaning_toc) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getCleaningEquipmentClassification(
                        selectedAsset?.cleaning_toc
                      )}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_internalno) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_internalno}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_enginetype) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.engineTypeLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getCleaningEngineType(
                        selectedAsset?.cleaning_enginetype
                      )}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_sweeping_width) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.sweepingWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_sweeping_width} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_suctionwidth) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.suctionWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_suctionwidth} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_workingwidth) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.workingWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_workingwidth} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_brush_pressure) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.brushPressure")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_brush_pressure}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(
                  selectedAsset?.cleaning_area_performance
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.areaPerformance")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_area_performance}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_fresh_tank) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.freshTank")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_fresh_tank} L
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_dirty_tank) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.dirtyTank")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_dirty_tank} L
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_container) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.container")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_container} L
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_weight) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.weight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_weight} kg
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_length) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.length")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_length} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_width) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.width")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_width} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_height) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.height")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_height} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_battery) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.battery")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.cleaning_battery}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.cleaning_available) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("cleaningEquipment.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(
                        selectedAsset?.cleaning_available
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* Construction Machinery specific fields */}
                {shouldDisplayValue(
                  selectedAsset?.construction_classification
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("constructionMachinery.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getConstructionMachineryClassification(
                        selectedAsset?.construction_classification
                      )}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_internalno) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.construction_internalno}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_enginetype) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.engineTypeLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getConstructionEngineType(
                        selectedAsset?.construction_enginetype
                      )}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_length) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("constructionMachinery.fields.length")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.construction_length} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_width) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("constructionMachinery.fields.width")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.construction_width} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_height) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("constructionMachinery.fields.height")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.construction_height} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_weight) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("constructionMachinery.fields.weight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.construction_weight} kg
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.construction_available) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("constructionMachinery.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(
                        selectedAsset?.construction_available
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* Spare Parts specific fields */}
                {/* {shouldDisplayValue(selectedAsset?.sparetype) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getSparePartClassification(selectedAsset?.sparetype)}
                    </span>
                  </div>
                )} */}

                {shouldDisplayValue(selectedAsset?.sparepart_id) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("spareParts.fields.sparepartId")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.sparepart_id}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.sparepart_internalno) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.sparepart_internalno}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.warehouse_number) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("spareParts.fields.warehouseNumber")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.warehouse_number}
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.sparepart_available) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("spareParts.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(
                        selectedAsset?.sparepart_available
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* Attachments specific fields */}
                {/* {selectedAsset?.attachment_toc && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_toc}
                    </span>
                  </div>
                )} */}

                {/* Lift Masts specific fields */}
                {/* {shouldDisplayValue(selectedAsset?.liftmast_classification) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.liftmast_classification}
                    </span>
                  </div>
                )} */}
                {shouldDisplayValue(selectedAsset?.mast_type) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("assets.fields.classification")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {getMastClassification(selectedAsset?.mast_type)}
                    </span>
                  </div>
                )}

                {/* Mast Internal No */}
                {shouldDisplayValue(selectedAsset?.mast_internalno) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.mast_internalno}
                    </span>
                  </div>
                )}

                {/* Mast Lift Height */}
                {shouldDisplayValue(selectedAsset?.mast_lift_height) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.liftHeight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.mast_lift_height} mm
                    </span>
                  </div>
                )}

                {/* Mast Closed Height */}
                {shouldDisplayValue(selectedAsset?.mast_closedheight) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.closedHeight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.mast_closedheight} mm
                    </span>
                  </div>
                )}

                {/* Mast Free Lift */}
                {shouldDisplayValue(selectedAsset?.mast_freelift) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.freeHeight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.mast_freelift} mm
                    </span>
                  </div>
                )}

                {/* Mast Fork Carriage Width */}
                {shouldDisplayValue(
                  selectedAsset?.mast_fork_carriage_width
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.forkCarriageLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.mast_fork_carriage_width} mm
                    </span>
                  </div>
                )}

                {/* Mast Additional Hydraulics */}
                {shouldDisplayValue(
                  selectedAsset?.mast_additional_hydraulics
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.additionalHydraulicsLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.mast_additional_hydraulics}
                    </span>
                  </div>
                )}

                {/* {shouldDisplayValue(selectedAsset?.year_of_manufacture) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(selectedAsset?.year_of_manufacture).toLocaleDateString()}
                    </span>
                  </div>
                )} */}
                {/* // Add these fields after the existing attachment_toc field (around line 1275) */}

                {/* Attachment ISO Class */}
                {shouldDisplayValue(selectedAsset?.attachment_isoclass) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.isoclass")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_isoclass}
                    </span>
                  </div>
                )}

                {/* Attachment Internal No */}
                {shouldDisplayValue(selectedAsset?.attachment_internalno) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("createAsset.placeholders.internNoLabel")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_internalno}
                    </span>
                  </div>
                )}

                {/* Opening Range From */}
                {shouldDisplayValue(selectedAsset?.attachment_rangefrom) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.openingRangeFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_rangefrom} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.attachment_rangeto) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.openingRangeTo")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_rangeto} mm
                    </span>
                  </div>
                )}

                {shouldDisplayValue(selectedAsset?.attachment_loadcentre) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.loadCentre")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_loadcentre} mm
                    </span>
                  </div>
                )}

                {/* Centre of Gravity */}
                {shouldDisplayValue(
                  selectedAsset?.attachment_center_gravity
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.centreOfGravity")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_center_gravity} mm
                    </span>
                  </div>
                )}

                {/* Lost Load Centre */}
                {shouldDisplayValue(
                  selectedAsset?.attachmemt_lost_load_center
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.lostLoadCentre")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachmemt_lost_load_center} mm
                    </span>
                  </div>
                )}

                {/* Weight */}
                {shouldDisplayValue(selectedAsset?.attachment_fork_weight) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.weight")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_fork_weight} kg
                    </span>
                  </div>
                )}

                {/* Overall Width */}
                {shouldDisplayValue(
                  selectedAsset?.attachment_overall_width
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.overallWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_overall_width} mm
                    </span>
                  </div>
                )}

                {/* Back Width */}
                {shouldDisplayValue(selectedAsset?.attachment_back_width) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.backWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_back_width} mm
                    </span>
                  </div>
                )}

                {/* Fork Length */}
                {shouldDisplayValue(selectedAsset?.attachment_fork_length) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.forkLength")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_fork_length} mm
                    </span>
                  </div>
                )}

                {/* Fork Width */}
                {shouldDisplayValue(selectedAsset?.attachment_fork_width) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.forkWidth")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_fork_width} mm
                    </span>
                  </div>
                )}

                {/* Fork Thickness */}
                {shouldDisplayValue(
                  selectedAsset?.attachment_fork_thickness
                ) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.forkThickness")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.attachment_fork_thickness} mm
                    </span>
                  </div>
                )}

                {/* Commission */}
                {shouldDisplayValue(selectedAsset?.commission) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.commission")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {selectedAsset?.commission}
                    </span>
                  </div>
                )}

                {/* Available From */}
                {shouldDisplayValue(selectedAsset?.attachment_available) && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                    <span className="text-xs sm:text-sm text-gray-600">
                      {t("attachments.fields.availableFrom")}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                      {new Date(
                        selectedAsset?.attachment_available
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Second Specifications Column */}
            {hasSecondColumnData() && (
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                <h3 className="font-semibold mb-2 sm:mb-3 text-gray-800">
                  {t("detailViewModel.technicalSpecification")}
                </h3>
                <div className="space-y-1 sm:space-y-2">
                  {selectedAsset?.rear_tire_size && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("createAsset.placeholders.rearTireSize")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.rear_tire_size}
                      </span>
                    </div>
                  )}

                  <div className="no-pdf">
                    {selectedAsset?.book_value && (
                      <div className="no-pdf grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                        <span className="text-xs sm:text-sm text-gray-600">
                          {t("createAsset.placeholders.bookValueLabel")} €
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                          {formatToEuropean(selectedAsset?.book_value)} €
                        </span>
                      </div>
                    )}
                  </div>

                  {!!selectedAsset?.battery_type && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("createAsset.placeholders.batteryType")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getBatteryType(selectedAsset?.battery_type)}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.battery_manufacturer && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryManufacturer")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right break-words">
                        {selectedAsset?.battery_manufacturer}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.batterycapacity && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("createAsset.placeholders.batteryCapacity")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.batterycapacity} Ah
                      </span>
                    </div>
                  )}

                  {!!selectedAsset?.batterylength && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryLength")}{" "}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.batterylength} mm
                      </span>
                    </div>
                  )}

                  {selectedAsset?.battery_location && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryLocation")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.battery_location}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.batterymodel && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryModel")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.batterymodel}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.battery_voltage && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryVoltage")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.battery_voltage} V
                      </span>
                    </div>
                  )}

                  {!!selectedAsset?.batteryweight && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryWeigth")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.batteryweight} kg
                      </span>
                    </div>
                  )}

                  {!!selectedAsset?.batterywidth && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.batteryWidth")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.batterywidth} mm
                      </span>
                    </div>
                  )}

                  {selectedAsset?.chargerLoadertype && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("assets.options.chargerType")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {getChargerType(selectedAsset?.chargerLoadertype)}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.chargerManufacturer && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.chargerManufacturer")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right break-words">
                        {selectedAsset?.chargerManufacturer}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.chargerAC && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.chargerAc")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.chargerAC}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.chargerCurrent && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.chargerAmp")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.chargerCurrent} A
                      </span>
                    </div>
                  )}

                  {selectedAsset?.chargerModel && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.chargerModel")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.chargerModel}
                      </span>
                    </div>
                  )}

                  {selectedAsset?.chargerVoltage && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("detailView.chargerNomindalVoltage")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {selectedAsset?.chargerVoltage} V
                      </span>
                    </div>
                  )}

                  {/* // ...existing code... */}

                  {selectedAsset?.additional_equipment &&
                    Array.isArray(selectedAsset?.additional_equipment) &&
                    selectedAsset.additional_equipment.length > 0 && (
                      <div className="grid grid-cols-1 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                        <span className="text-xs sm:text-sm text-gray-600">
                          {t("detailView.additionalEquipment")}
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-gray-800 break-words overflow-wrap-anywhere leading-relaxed">
                          {Array.isArray(selectedAsset?.additional_equipment) &&
                          selectedAsset.additional_equipment.length > 0
                            ? selectedAsset.additional_equipment
                                .map((item) => {
                                  let key = "";
                                  if (typeof item === "number") {
                                    key = ADD_EQUIPMENT_OPTIONS[item - 1] || "";
                                  } else if (typeof item === "string") {
                                    key = item;
                                  } else if (item && typeof item === "object") {
                                    key = item.name || item.label || "";
                                  }
                                  if (!key || typeof key !== "string")
                                    return null;
                                  const translationKey = `assets.options.${key
                                    .replace(/\s+/g, "_")
                                    .replace(/-/g, "_")
                                    .replace(/\(|\)/g, "")
                                    .replace(/[^a-zA-Z0-9_]/g, "")
                                    .toLowerCase()}`;
                                  const translated = t(translationKey);
                                  return translated === translationKey
                                    ? key
                                    : translated;
                                })
                                .filter(Boolean)
                                .join(", ")
                            : t("common.noData")}
                        </span>
                      </div>
                    )}

                  {selectedAsset?.end_user_price && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 border-b border-gray-200 pb-1">
                      <span className="text-xs sm:text-sm text-gray-600">
                        {t("createAsset.placeholders.endUserPriceLabel")}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-gray-800 text-right">
                        {formatToEuropean(selectedAsset?.end_user_price)} €
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="my-6 w-full" id="remark-section">
          <h2 className="text-lg font-semibold mb-3 sm:mb-4 handwriting bg-gray-600 text-white p-1">
            {t("detailViewModel.remark")}
          </h2>
          <input
            id="remark-input"
            type="text"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t("detailViewModel.enterRemark")}
          />
        </div>

        {/* Handwritten Notes Section */}
        <div className="border-t border-gray-200 pt-6 ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Service Calendar History */}
            <div className="no-pdf">
              <BookValueHistory t={t} id={id} viewOnly={true} />
            </div>
            <div className="no-pdf">
              <ContractHistoryView t={t} id={id} isEditable={false} />
            </div>
          </div>

          {/* Bottom Signature Line */}
          <div className="mt-8 pt-4">
            <div className="w-full h-1 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForkliftDetailView;
